import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useClaimsDashboard from "../hook/useDashboard.mjs";
import PageShell from "../../components/PageShell.mjs";
import { getClaimStatus, claimantGroupOptions, claimantGroupLabel }
  from "../../utils/statusConfig.mjs";


const ClaimsDashboard = () => {
  const navigate = useNavigate();
  const {
    loading,
    error,
    claimType,
    setClaimType,
    statusFilter,
    setStatusFilter,
    activeClaims,
    typeClaims,
    refreshClaims,
    selectedIds,
    toggleSelect,
    selectAll,
    clearSelection,
    deleteSelected,
    deleting,
    isDraftView
  } = useClaimsDashboard();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleViewClaim = (claimId) => {
    if (claimType === 'perdiem') {
        navigate(`/claim/perdiem-lodging/${claimId}`);
    } else {
        navigate(`/${claimType}/claims/${claimId}`);
    }
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    await deleteSelected();
  };

  const allVisibleSelected = activeClaims.length > 0 && activeClaims.every(c => selectedIds.has(c._id));

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };


  const getClaimAmount = (claim) => {
    if (!claim) return 0;
    // Prefer the USD-normalized total. Expense claims can mix currencies, so the
    // raw totalAmount would sum unconverted amounts (wrong); totalUsdAmount is the
    // correct USD figure. Per-diem amounts are already USD (totalClaim).
    return claim.totalUsdAmount ?? claim.totalClaim ?? claim.totalAmount ?? claim.amount ?? 0;
  };

  const shellProps = { title: 'Claims Dashboard', subtitle: 'Track and manage your expense claims' };

  if (loading) {
    return (
      <PageShell {...shellProps}>
        <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading claims data...</p>
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell {...shellProps}>
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-auto text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Claims</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={refreshClaims}
            className="px-6 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors"
          >
            Try Again
          </button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell {...shellProps}>
        {/* Filters Section */}
        <div className="dashboard-filters mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 sm:gap-6">
            {/* Claim Type Filter */}
            <div>
              <h3 className="dashboard-filters-title mb-3">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Claim Type
              </h3>
              <div className="grid grid-cols-2 sm:flex gap-3">
                <button
                  className={`min-h-[44px] px-5 py-2.5 rounded-xl font-medium transition-all duration-300 ${claimType === 'perdiem' ? 'bg-gradient-to-r from-stanford-red to-stanford-cardinal-dark text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'}`}
                  onClick={() => setClaimType('perdiem')}
                >
                  Per Diem
                </button>
                <button
                  className={`min-h-[44px] px-5 py-2.5 rounded-xl font-medium transition-all duration-300 ${claimType === 'expense' ? 'bg-gradient-to-r from-stanford-red to-stanford-cardinal-dark text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'}`}
                  onClick={() => setClaimType('expense')}
                >
                  Expense
                </button>
              </div>
            </div>

            {/* Status filter.
                A dropdown, not pills: the options are data-dependent and their
                labels are sentences, so the pill row grew with the data and
                wrapped. Each option carries its count, which a pill row could
                not show without getting longer still. */}
            <div className="lg:min-w-[280px]">
              <h3 className="dashboard-filters-title mb-3">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Status
              </h3>
              {(() => {
                const options = claimantGroupOptions(typeClaims);
                return (
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    aria-label="Filter claims by status"
                    className="w-full min-h-[44px] px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-base sm:text-sm text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-stanford-red focus:border-stanford-red"
                  >
                    <option value="">All claims ({typeClaims.length})</option>
                    {options.map(({ value, label, count }) => (
                      <option key={value} value={value}>{label} ({count})</option>
                    ))}
                  </select>
                );
              })()}
              {statusFilter && (
                <p className="text-xs text-gray-500 mt-2">
                  {claimantGroupOptions(typeClaims).find(g => g.value === statusFilter)?.description || ''}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Bulk Delete Toolbar - only visible in draft view */}
        {isDraftView && activeClaims.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-gray-200 rounded-xl px-4 sm:px-5 py-3 mb-4 shadow-sm">
            <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 accent-stanford-red cursor-pointer"
                checked={allVisibleSelected}
                onChange={() => allVisibleSelected ? clearSelection() : selectAll(activeClaims)}
              />
              <span className="text-sm text-gray-600">
                {selectedIds.size > 0
                  ? `${selectedIds.size} draft${selectedIds.size !== 1 ? 's' : ''} selected`
                  : 'Select all drafts'}
              </span>
            </label>
            {selectedIds.size > 0 && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
                className="flex items-center gap-2 min-h-[44px] px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete {selectedIds.size} Draft{selectedIds.size !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {activeClaims.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">
                {statusFilter
                  ? `No ${claimantGroupLabel(statusFilter).toLowerCase()} ${claimType === 'perdiem' ? 'per diem' : 'expense'} claims.`
                  : `No ${claimType === 'perdiem' ? 'per diem' : 'expense'} claims yet.`}
              </p>
            </div>
          ) : (
            <>
            {/* Phones: one card per claim. The four-column table pushed the
                only action (View Details) off-screen at 360px. */}
            <ul className="md:hidden divide-y divide-gray-200">
              {activeClaims.map(claim => {
                const status = getClaimStatus(claim.status, { self: true });
                const selected = isDraftView && selectedIds.has(claim._id);
                return (
                  <li key={claim._id} className={`flex items-stretch ${selected ? 'bg-red-50' : ''}`}>
                    {isDraftView && (
                      <label className="flex items-center pl-4 pr-1 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-5 h-5 accent-stanford-red cursor-pointer"
                          checked={selectedIds.has(claim._id)}
                          onChange={() => toggleSelect(claim._id)}
                          aria-label={`Select draft from ${formatDate(claim.createdAt)}`}
                        />
                      </label>
                    )}
                    <button
                      type="button"
                      onClick={() => handleViewClaim(claim._id)}
                      className="flex-1 min-w-0 flex items-center gap-3 px-4 py-4 text-left active:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-stanford-red"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-base font-semibold text-gray-900 tabular-nums">{formatCurrency(getClaimAmount(claim))}</span>
                          <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(claim.createdAt)}</span>
                        </div>
                        <span className={`inline-flex items-center mt-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${status.badge}`}>
                          {status.label}
                        </span>
                        {claim.filedOnBehalfByName && (
                          <span className="block mt-1 text-xs text-indigo-700">
                            Filed by {claim.filedOnBehalfByName}
                          </span>
                        )}
                      </div>
                      <svg className="shrink-0 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="sr-only">View details</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    {isDraftView && (
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-stanford-red cursor-pointer"
                          checked={allVisibleSelected}
                          onChange={() => allVisibleSelected ? clearSelection() : selectAll(activeClaims)}
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-sm font-semibold">Date Created</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeClaims.map(claim => (
                    <tr
                      key={claim._id}
                      className={`border-b border-gray-200 hover:bg-gray-50 ${isDraftView && selectedIds.has(claim._id) ? 'bg-red-50' : ''}`}
                    >
                      {isDraftView && (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-stanford-red cursor-pointer"
                            checked={selectedIds.has(claim._id)}
                            onChange={() => toggleSelect(claim._id)}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-700">{formatDate(claim.createdAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-medium tabular-nums">{formatCurrency(getClaimAmount(claim))}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getClaimStatus(claim.status, { self: true }).badge}`}>
                          {getClaimStatus(claim.status, { self: true }).label}
                        </span>
                        {/* A claim you did not write is alarming unless it says
                            who did. The row has no other per-claim marker, so
                            this sits under the status chip. */}
                        {claim.filedOnBehalfByName && (
                          <span
                            className="block mt-1 text-xs text-indigo-700"
                            title={`Filed for you by ${claim.filedOnBehalfByName}. Check the details and tell them if anything is wrong.`}
                          >
                            Filed by {claim.filedOnBehalfByName}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          className="px-4 py-2 text-sm text-stanford-red border border-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors"
                          onClick={() => handleViewClaim(claim._id)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Draft Claims</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to permanently delete <span className="font-semibold text-red-600">{selectedIds.size} draft {claimType} claim{selectedIds.size !== 1 ? 's' : ''}</span>? This action cannot be undone.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="min-h-[44px] px-5 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="min-h-[44px] px-5 py-2 text-sm font-medium bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};


export default ClaimsDashboard;
